import React, {
    Fragment,
    useContext,
    useEffect,
    useMemo,
    useReducer,
} from "react";
import Toolbar, { ToolbarGroup } from "components/Toolbar/Toolbar";
import ToolbarButton from "components/Toolbar/ToolbarButton";
import {
    SaveIcon,
    LockClosedIcon,
    LockOpenIcon,
    XIcon,
    EyeIcon,
    BanIcon,
    TrashIcon,
    ArrowCircleUpIcon,
    SupportIcon,
} from "@heroicons/react/outline";
import { ContentContext } from "context/Content";
import { useAuth } from "hooks/Auth";
import {
    apiPost,
    apiDel,
    apiGet,
    apiPut,
    messageFromException,
} from "helpers/Request";
import { useFormikContext } from "formik";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { removeItem as removeRecentItem } from "redux/actions/recent-items";
import { useContextualHelp } from "hooks/Help";
import { useRegisterActions } from "kbar";
import { toast } from "react-hot-toast";
import { modal } from "components/HeadlessModal";
import SecondaryButton from "components/Buttons/SecondaryButton";
import DangerButton from "components/Buttons/DangerButton";
import * as KVStore from "helpers/KVStore";

const reducer = (oldState, newState) => {
    return Object.assign({}, oldState, newState);
};

function ArticleToolbar(props) {
    const { state: contentState, setState: setContentState } =
        useContext(ContentContext);
    const auth = useAuth();
    const history = useHistory();
    const dispatch = useDispatch();
    const [state, setState] = useReducer(reducer, {
        locked: false,
        userEditing: false,
        blockingButton: false,
        generatingPreview: false,
    });
    const { submitForm } = useFormikContext();
    const contextualHelp = useContextualHelp();

    const allKbarActions = [
        {
            id: "articleCheckout",
            name: "Check Out",
            keywords: "checkout",
            perform: () => {
                checkOut();
            },
        },
        {
            id: "articleCheckin",
            name: "Check In",
            keywords: "checkin",
            perform: () => {
                checkIn();
            },
        },
        {
            id: "articleSaveCheckin",
            name: "Save and Check In",
            keywords: "save checkin",
            perform: () => {
                saveCheckIn();
            },
        },
        {
            id: "articlePreview",
            name: "Preview",
            keywords: "preview",
            perform: () => {
                openPreview();
            },
        },
    ];

    const lockState = useMemo(() => {
        if (!contentState.content || !contentState.content.uuid) {
            return "new";
        }
        if (
            contentState.content.lock &&
            contentState.content.lock.user.uuid === auth.user.uuid
        ) {
            return "locked-self";
        } else if (
            contentState.content.lock &&
            contentState.content.lock.user.uuid !== auth.user.uuid
        ) {
            return "locked-other";
        } else {
            return "available";
        }
    }, [contentState.content, auth.user]);

    const kbarActions = useMemo(() => {
        return allKbarActions.filter((action) => {
            if (action.id === "articleCheckout" && lockState === "available") {
                return true;
            } else if (
                (action.id === "articleCheckin" ||
                    action.id === "articleSaveCheckin") &&
                lockState === "locked-self"
            ) {
                return true;
            } else if (action.id === "articlePreview" && lockState !== "new") {
                return true;
            }

            return false;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentState.content, lockState]);

    useRegisterActions(kbarActions, [kbarActions]);

    const checkOut = () => {
        if (!contentState.content.uuid) {
            console.debug("Cannot check out; no uuid");
            return;
        }
        if (contentState.content.lock) {
            console.deubg("Cannot check out; already locked");
            return;
        }

        const id = toast.loading("Checking out...");
        setState({ blockingButton: "check-out" });
        setContentState({ preformingStateUpdate: true });
        apiPost("/v4/lock-request", auth.publication, {
            srn: contentState.content.srn,
        })
            .then((resp) => {
                contentState.requestReload();
                setContentState({ preformingStateUpdate: false });
                setState({ blockingButton: false });
                toast.success("Checked out", { id, removeDelay: 2000 });
            })
            .catch((err) => {
                setContentState({ preformingStateUpdate: false });
                setState({ blockingButton: false });
                toast.dismiss(id);
                modal.error({
                    title: "Unable to check out",
                    children: messageFromException(err),
                });
            });
    };

    const clearLock = () => {
        if (!contentState.content.uuid) {
            return;
        }
        if (!contentState.content.lock || !auth.user.can("admin")) {
            return;
        }
        modal.warning({
            title: "Clear Lock",
            children: (
                <Fragment>
                    Forcing a lock to clear will make this item immediately
                    available, but discard any pending changes. Are you sure you
                    want to continue?
                </Fragment>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={clearLockConfirm}>
                        Clear Lock
                    </DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const clearLockConfirm = () => {
        setState({ blockingButton: "clear-lock" });
        setContentState({ preformingStateUpdate: true });
        modal.update({
            disabled: true,
        });

        const id = toast.loading("Clearing lock...");
        apiDel(
            `/v4/lock-request/${contentState.content.lock.uuid}?force_override=${auth.user.uuid}`,
            auth.publication
        )
            .then((resp) => {
                contentState.requestReload();
                setContentState({ preformingStateUpdate: false });
                setState({ blockingButton: false });
                modal.dismiss();
                toast.success("Lock cleared", { id, removeDelay: 2000 });
            })
            .catch((err) => {
                modal.dismiss();
                modal.error({
                    title: "Unable to clear lock",
                    children: messageFromException(err),
                });
                toast.dismiss(id);
            });
    };

    const discardChanges = () => {
        if (!contentState.content.uuid) {
            return;
        }

        modal.warning({
            title: "Discard Changes",
            children: (
                <Fragment>
                    This will discard any unsaved changes and release the lock
                    for this item.
                </Fragment>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={checkIn}>
                        Discard changes
                    </DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const checkIn = () => {
        if (!contentState.content.uuid) {
            console.debug("Cannot unlock, no uuid");
            return;
        }
        if (
            !contentState.content.lock ||
            contentState.content.lock.user.uuid !== auth.user.uuid
        ) {
            console.debug("Cannot unlock, no lock attached");
            return;
        }

        setState({ blockingButton: "check-in" });
        setContentState({ preformingStateUpdate: true });
        KVStore.remove(contentState.content.uuid);

        const id = toast.loading("Checking in...");
        apiDel(
            "/v4/lock-request/" + contentState.content.lock.uuid,
            auth.publication
        )
            .then((resp) => {
                contentState.requestReload();
                setContentState({ preformingStateUpdate: false });
                setState({ blockingButton: false });
                toast.success("Checked in", { id, removeDelay: 2000 });
            })
            .catch((err) => {
                toast.dismiss(id);
                setContentState({ preformingStateUpdate: false });
                setState({ blockingButton: false });
                modal.error({
                    title: "Unable to check in",
                    children: messageFromException(err),
                });
            });
    };

    const saveCheckIn = () => {
        if (!contentState.content.uuid) {
            console.debug("Cannot unlock, no uuid");
            return;
        }
        if (
            !contentState.content.lock ||
            contentState.content.lock.user.uuid !== auth.user.uuid
        ) {
            console.debug("Cannot lock, no lock, or not locked by user");
            return;
        }

        setState({ blockingButton: "save-check-in" });
        setContentState({ preformingStateUpdate: true });
        KVStore.remove(contentState.content.uuid);
        submitForm().then(() => {
            const id = toast.loading("Checking in...");
            apiDel(
                "/v4/lock-request/" + contentState.content.lock.uuid,
                auth.publication
            )
                .then((resp) => {
                    contentState.requestReload();
                    setContentState({ preformingStateUpdate: false });
                    setState({ blockingButton: false });
                    toast.success("Checked in", { id, removeDelay: 2000 });
                })
                .catch((err) => {
                    toast.dismiss(id);
                    setContentState({ preformingStateUpdate: false });
                    setState({ blockingButton: false });
                    modal.error({
                        title: "Unable to check in",
                        children: messageFromException(err),
                    });
                });
        });
    };

    const del = () => {
        if (!contentState.content.uuid) {
            return;
        }
        if (
            !contentState.content.lock ||
            contentState.content.lock.user.uuid !== auth.user.uuid
        ) {
            return;
        }

        const uuid = contentState.content.uuid;

        modal.warning({
            title: "Delete Items",
            children: (
                <Fragment>
                    Deleting will immediately remove this item and all saved
                    versions. This cannot be undone.
                </Fragment>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={() => delConfirm(uuid)}>
                        Delete
                    </DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const delConfirm = (uuid) => {
        setState({ blockingButton: "delete" });
        setContentState({ preformingStateUpdate: true });
        modal.update({
            disabled: true,
        });
        KVStore.remove(uuid);
        const id = toast.loading("Deleting...");
        apiDel(`/v4/content/${uuid}`, auth.publication)
            .then(() => {
                dispatch(removeRecentItem(contentState.content));
                toast.success("Deleted", { id, removeDelay: 2000 });
                modal.dismiss();
                history.push("/app/dashboard");
            })
            .catch((err) => {
                setState({ blockingButton: false });
                setContentState({ preformingStateUpdate: false });
                modal.dismiss();
                modal.error({
                    title: "Unable to delete",
                    children: messageFromException(err),
                });
            });
    };

    const forceSync = async () => {
        if (!contentState.content.uuid) {
            return;
        }
        const id = toast.loading("Forcing sync...");
        const resp = await apiPut(
            `/v4/content/${contentState.content.uuid}/force-sync/`,
            auth.publication
        );
        if (resp) {
            toast.success("Sync queued", { id, removeDelay: 2000 });
        } else {
            toast.dismiss(id);
            modal.error({
                title: "Unable to force sync",
                children: (
                    <Fragment>
                        Unable to force sync, this is usually a temporary error.
                        Please try again.
                    </Fragment>
                ),
            });
        }
    };
    const openPreview = async () => {
        if (!contentState.content.uuid) {
            return;
        }
        setState({ generatingPreview: true });
        const id = toast.loading("Generating preview...");
        const resp = await apiGet(
            `/v4/content/${contentState.content.uuid}/preview/`,
            auth.publication
        );
        if (resp) {
            const url = resp["message"];
            window.open(url, "preview");
            toast.dismiss(id);
            setState({ generatingPreview: false });
        } else {
            toast.dismiss(id);
            setState({ generatingPreview: false });
            modal.error({
                title: "Unable to generate preview",
                children: (
                    <Fragment>
                        Unable to generate preview, this is usually a temporary
                        error. Please try again.
                    </Fragment>
                ),
            });
        }
    };

    useEffect(() => {
        if (!contentState.content || !contentState.content.uuid) {
            setState({ locked: false, userEditing: true });
            return;
        }
        if (
            contentState.content.lock &&
            contentState.content.lock.user.uuid === auth.user.uuid
        ) {
            setState({ locked: true, userEditing: true });
        } else if (
            contentState.content.lock &&
            contentState.content.lock.user.uuid !== auth.user.uuid
        ) {
            setState({ locked: true, userEditing: false });
        } else {
            setState({ locked: false, userEditing: false });
        }
    }, [contentState.content, auth.user]);

    return (
        <Toolbar className="z-10 overflow-y-auto">
            <ToolbarGroup>
                <ToolbarButton
                    icon={<SaveIcon />}
                    type="submit"
                    disabled={
                        state.userEditing && !contentState.preformingStateUpdate
                            ? false
                            : true
                    }
                    blocking={contentState.saving && !state.blockingButton}
                >
                    Save
                </ToolbarButton>
                <ToolbarButton
                    icon={<LockClosedIcon />}
                    blocking={state.blockingButton === "save-check-in"}
                    onClick={saveCheckIn}
                    disabled={
                        state.userEditing &&
                        contentState.content.uuid &&
                        !contentState.preformingStateUpdate
                            ? false
                            : true
                    }
                >
                    Save & Check-in
                </ToolbarButton>
                <ToolbarButton
                    icon={<XIcon />}
                    disabled={
                        state.userEditing &&
                        contentState.content.uuid &&
                        !contentState.preformingStateUpdate
                            ? false
                            : true
                    }
                    hidden={!state.userEditing && contentState.content.uuid}
                    onClick={discardChanges}
                    blocking={state.blockingButton === "check-in"}
                >
                    Discard Changes
                </ToolbarButton>
                <ToolbarButton
                    icon={<LockOpenIcon />}
                    disabled={
                        !state.userEditing &&
                        !state.locked &&
                        !contentState.preformingStateUpdate
                            ? false
                            : true
                    }
                    hidden={state.userEditing && state.locked}
                    onClick={checkOut}
                    blocking={state.blockingButton === "check-out"}
                >
                    Check-out
                </ToolbarButton>
            </ToolbarGroup>
            <ToolbarGroup>
                <ToolbarButton
                    icon={<EyeIcon />}
                    disabled={
                        !contentState.content.uuid || state.generatingPreview
                    }
                    onClick={openPreview}
                >
                    Preview
                </ToolbarButton>
                <ToolbarButton
                    icon={<BanIcon />}
                    disabled={
                        state.locked &&
                        !contentState.preformingStateUpdate &&
                        auth.user.can("admin")
                            ? false
                            : true
                    }
                    blocking={state.blockingButton === "unlock"}
                    onClick={clearLock}
                >
                    Unlock
                </ToolbarButton>
                <ToolbarButton
                    icon={<ArrowCircleUpIcon />}
                    disabled={!contentState.content.uuid}
                    onClick={forceSync}
                >
                    Sync
                </ToolbarButton>
                <ToolbarButton
                    icon={<TrashIcon />}
                    onClick={del}
                    disabled={
                        state.locked &&
                        !contentState.preformingStateUpdate &&
                        auth.user.can("content", "delete")
                            ? false
                            : true
                    }
                    blocking={state.blockingButton === "delete"}
                >
                    Delete
                </ToolbarButton>
            </ToolbarGroup>
            <ToolbarGroup className="ml-auto">
                <ToolbarButton
                    icon={<SupportIcon />}
                    onClick={() => {
                        contextualHelp.showContextualHelp(
                            `/${
                                contentState.content.type
                                    ? contentState.content.type
                                    : "article"
                            }/index.html`
                        );
                    }}
                    className="text-green-600"
                >
                    Help
                </ToolbarButton>
            </ToolbarGroup>
        </Toolbar>
    );
}

export default ArticleToolbar;
